import _ from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import * as budgetApi from '../../lib/api/budget';

// mui
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

// components
import { getNumberComma } from '../../utils/numberUtils';
import NumberFormatText from '../common/form/NumberFormatText';
import FormLabel from '../common/form/FormLabel';
//----------------------------------------------
const defaultMi = {
  nationalPension: 0,
  healthInsurance: 0,
  employeInsurance: 0,
  accidentInsurance: 0,
};
//----------------------------------------------
const MonthSalaryForm = ({
  currentSalaryData,
  currentYear,
  currentUser,
  onLoadData,
  miPercents,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [enteredSalary, setEnteredSalary] = useState(0);
  const [inputSalaryData, setInputSalaryData] = useState(null);
  const [inputMIData, setInputMIData] = useState(null);

  useEffect(() => {
    if (currentSalaryData && currentUser && currentYear) {
      const sas =
        currentSalaryData?.salaries?.length === 0
          ? defaultMonthSalary()
          : _.uniqBy(currentSalaryData?.salaries, 'month');
      const mi = currentSalaryData?.miData
        ? currentSalaryData?.miData
        : defaultMi;

      const newData = {
        year: currentYear,
        usrNm: currentUser.usrNm,
        usrUuid: currentUser.usrUuid,
        salary: null,
        salaries: sas,
      };

      setInputSalaryData(newData);
      setInputMIData(mi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentYear, currentSalaryData]);

  const total = _.sumBy(inputSalaryData?.salaries, (sa) => +sa.salary);
  const sumValues = (obj) => Object.values(obj).reduce((a, b) => a + b, 0);
  const mTotal =
    inputMIData !== null && inputMIData !== undefined
      ? sumValues(inputMIData)
      : 0;

  const defaultMonthSalary = () => {
    const salary = 0;
    const monthSalary = [];
    for (let i = 1; i <= 12; i++) {
      let month = { month: i, salary: +salary };
      monthSalary.push(month);
    }
    return monthSalary;
  };

  const makeMonthSalary = () => {
    const salary = enteredSalary;
    const monthSalary = [];
    for (let i = 1; i <= 12; i++) {
      let month = { month: i, salary: +salary };
      monthSalary.push(month);
    }

    setInputSalaryData((prevData) => ({
      ...prevData,
      salary: enteredSalary,
      salaries: monthSalary,
    }));
  };

  const handleChangeMonthSalary = (newValue, sa) => {
    // 신규 값으로 update
    setInputSalaryData((prevData) => {
      const newSalary = {
        month: sa.month,
        salary: Number(newValue),
      };
      const filteredData = prevData.salaries.filter(
        (salary) => salary.month !== sa.month,
      );
      const orderedData = _.orderBy([...filteredData, newSalary], 'month');
      return {
        ...prevData,
        salaries: orderedData,
      };
    });
  };

  const handleReset = () => {
    setEnteredSalary(0);
    makeMonthSalary();
    setInputMIData(defaultMi);
  };

  const handleChangeMI = (name, newValue) => {
    setInputMIData((prevData) => ({ ...prevData, [name]: Number(newValue) }));
  };

  // 4 대 보험 계산

  const handleCalcMI = () => {
    const nationalPension = Math.floor(
      total * (miPercents?.nationalPension / 100),
    );
    const healthInsurance = Math.floor(
      total * (miPercents?.healthInsurance / 100),
    );
    const employeInsurance = Math.floor(
      total * (miPercents?.employeInsurance / 100),
    );
    const accidentInsurance = Math.floor(
      total * (miPercents?.accidentInsurance / 100),
    );

    setInputMIData({
      nationalPension,
      healthInsurance,
      employeInsurance,
      accidentInsurance,
    });
  };

  const handleSave = async () => {
    
    try {
      const res = await budgetApi.addIndividualSalary({
        ...inputSalaryData,
        miData: inputMIData,
      });
      if (res.data.result === 'success' && res.data?.info) {
        enqueueSnackbar(`${currentUser?.usrNm}의 급여가 저장되었습니다`, {
          variant: 'success',
        });

        // onLoadData
        onLoadData();
      } else {
        const msg = res.data.message
          ? res.data.message
          : `${currentUser?.usrNm}의 급여 저장에 실패하였습니다.`;
        enqueueSnackbar(msg, {
          variant: 'error',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  return (
    <Box>
      <Stack
        spacing={1}
        sx={{
          width: '100%',
          bgcolor: 'grey.50',
          borderColor: 'grey.300',
          p: 1,
          m: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            px: 4,
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            [{currentYear}]년 {currentUser?.usrNm} 의 월 급여 리스트
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ width: '20%', textAlign: 'center' }}>
            <Typography variant="subtitle2">월 급여 입력</Typography>
          </Box>
          <Box>
            <TextField
              size="small"
              required
              variant="outlined"
              fullWidth
              id="salary"
              name="salary"
              value={enteredSalary}
              onChange={(e) => {
                setEnteredSalary(e.target.value);
              }}
              InputProps={{
                inputComponent: NumberFormatText,
              }}
            />
          </Box>
          <Box>
            <Button variant="outlined" color="info" onClick={makeMonthSalary}>
              배치
            </Button>
          </Box>
        </Stack>
        <Grid
          container
          spacing={1}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 2,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          {inputSalaryData?.salaries?.length > 0 &&
            inputSalaryData?.salaries.map((sa) => (
              <Fragment key={sa.month}>
                <Grid item xs={1} sx={{ textAlign: 'right' }}>
                  <Typography variant="subtitle2">{sa.month}월</Typography>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    size="small"
                    required
                    variant="outlined"
                    fullWidth
                    id={`salaries_${sa.month}`}
                    name={`salaries_${sa.month}`}
                    value={sa.salary}
                    onChange={(e) =>
                      handleChangeMonthSalary(e.target.value, sa)
                    }
                    InputProps={{
                      inputComponent: NumberFormatText,
                    }}
                  />
                </Grid>
              </Fragment>
            ))}
        </Grid>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
        >
          <Typography variant="subtitle2">급여 합계</Typography>
          <Typography variant="subtitle2">
            {getNumberComma(total)} 원
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={1}
        sx={{
          width: '100%',
          bgcolor: 'grey.50',
          borderColor: 'grey.300',
          p: 1,
          m: 1,
        }}
      >
        <Stack spacing={2} sx={{ px: 2 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              px: 4,
              py: 1,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                [{currentYear}]년 {currentUser?.usrNm} 의 4대 보험 금액
              </Typography>
            </Box>
            <Box>
              <Button variant="outlined" color="info" onClick={handleCalcMI}>
                4대 보험 계산
              </Button>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              py: 1,
            }}
          >
            <Box textAlign="center">
              <FormLabel sx={{ px: 1 }}>국민연금</FormLabel>
            </Box>
            <Box>
              <TextField
                size="small"
                required
                variant="outlined"
                fullWidth
                id="nationalPension"
                name="nationalPension"
                value={inputMIData?.nationalPension}
                onChange={(e) =>
                  handleChangeMI('nationalPension', e.target.value)
                }
                InputProps={{
                  inputComponent: NumberFormatText,
                }}
              />
            </Box>
            <Box textAlign="center">
              <FormLabel sx={{ px: 1 }}>건강보험</FormLabel>
            </Box>
            <Box>
              <TextField
                size="small"
                required
                variant="outlined"
                fullWidth
                id="healthInsurance"
                name="healthInsurance"
                value={inputMIData?.healthInsurance}
                onChange={(e) =>
                  handleChangeMI('healthInsurance', e.target.value)
                }
                InputProps={{
                  inputComponent: NumberFormatText,
                }}
              />
            </Box>
            <Box textAlign="center">
              <FormLabel sx={{ px: 1 }}>고용보험</FormLabel>
            </Box>
            <Box>
              <TextField
                size="small"
                required
                variant="outlined"
                fullWidth
                id="employeInsurance"
                name="employeInsurance"
                value={inputMIData?.employeInsurance}
                onChange={(e) =>
                  handleChangeMI('employeInsurance', e.target.value)
                }
                InputProps={{
                  inputComponent: NumberFormatText,
                }}
              />
            </Box>
            <Box textAlign="center">
              <FormLabel sx={{ px: 1 }}>산재보험</FormLabel>
            </Box>
            <Box>
              <TextField
                size="small"
                required
                variant="outlined"
                fullWidth
                id="accidentInsurance"
                name="accidentInsurance"
                value={inputMIData?.accidentInsurance}
                onChange={(e) =>
                  handleChangeMI('accidentInsurance', e.target.value)
                }
                InputProps={{
                  inputComponent: NumberFormatText,
                }}
              />
            </Box>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            px: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2">4대보험 합계</Typography>
          <Typography variant="subtitle2">
            {getNumberComma(mTotal)}원
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={2}
        sx={{ py: 1 }}
      >
        <Button variant="contained" color="warning" onClick={handleReset}>
          초기화
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          저장
        </Button>
      </Stack>
    </Box>
  );
};

export default MonthSalaryForm;
